/**
 * D247 — the fourth member of the `.popover` family: a three-way exit branch about unsaved work.
 *
 * The set is split by **what the dialog is**, not by which props happen to be passed:
 *
 * - `Popover` — a question that collects a value (cancel + confirm around an input).
 * - `ConfirmPopover` — a question with a yes and a no.
 * - `AlertPopover` — an answer. One dismiss action, and no way to add a confirm.
 * - `UnsavedChangesPopover` — a question with **three** answers, one of them destructive and
 *   deliberately *not* the primary. This one.
 *
 * **It is a sibling rather than `ConfirmPopover` with its `secondaryAction` filled in**, and that
 * is the same rule D143/D144 drew rather than a new one. That slot is documented for exactly one
 * case — *a retry, when the dialog is waiting on a read it needs … a way of **not** proceeding
 * yet* — and discarding edits is the opposite: a destructive proceed. Pressed into service here,
 * three of `ConfirmPopover`'s own meanings would have to be read backwards at once: its confirm is
 * `.btn-primary.popover-danger`, which would paint *save* as the destruction; `onCancel` would
 * have to mean *stay*; and `.popover-actions button { flex: 1 }` puts all three in one row as
 * equal-width peers.
 *
 * So the actions **stack** here (`.popover-leave-actions`), in the order save → discard → stay,
 * for two reasons that hold independently. **The weighting**: three equal cells present a primary
 * action, a destructive one and a safe dismissal as peers, and put the destructive one a mis-tap
 * from either neighbour — a column states the order and lets each button carry its own weight.
 * **And the width**: `flex: 1` gives about 90px per button on a 320px screen, which
 * "ביטול שינויים" does not fit in. The weighting is why a row would be wrong even if every label
 * were short; the width is why one is not available anyway.
 *
 * **The stay label is `common.stayHere`, NOT `common.back`, though every locale now spells the two
 * identically.** That collision is the reason to keep the keys apart rather than an argument for
 * merging them: `common.back` is the app bar's own arrow, which **leaves** the screen, and here the
 * same word means **stay on it** — the two are rendered side by side, and one string bound to both
 * is a retranslation away from inverting this dialog in silence. A language whose *go back* and
 * *remain here* are different words would also have nowhere to say so. `discardChanges` is its own
 * key on the same logic: `common.cancel` reads as *dismiss this dialog* everywhere else in the app,
 * which is the opposite of what this button does.
 *
 * **Focus lands on stay, and both the backdrop and `Escape` answer stay** — the one answer that
 * loses nothing. `ConfirmPopover` focuses cancel for the same reason; here the stakes are a
 * resident's typed work rather than a delete, and a stray Enter must not spend it.
 *
 * **It registers no history entry of its own** (D247, `useLeaveGuard`): the guard entry it replaces
 * *was* the entry, and pushing one here would be a `pushState` issued from inside a `popstate`
 * handler — D145's skippable-entry trap. Render it as a *sibling* of `<Drawer>`, never a child,
 * for the z-index reason its three siblings carry.
 */
import { useEffect, useRef } from 'react'

export function UnsavedChangesPopover({
  title,
  body,
  saveLabel,
  discardLabel,
  stayLabel,
  onSave,
  onDiscard,
  onStay,
  saveDisabled = false,
  error,
}: {
  readonly title: string
  readonly body: string
  readonly saveLabel: string
  readonly discardLabel: string
  readonly stayLabel: string
  onSave(): void
  onDiscard(): void
  onStay(): void
  /** In flight, or refused for a reason the screen has already reported. */
  readonly saveDisabled?: boolean | undefined
  readonly error?: string | undefined
}): JSX.Element {
  const stayRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    stayRef.current?.focus()
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') onStay()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onStay])

  return (
    <div className="popover-backdrop" onClick={onStay}>
      <div
        className="popover"
        role="dialog"
        aria-modal="true"
        aria-labelledby="unsaved-popover-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="popover-title" id="unsaved-popover-title">
          {title}
        </h2>
        <p className="popover-body">{body}</p>
        {error === undefined ? null : <p className="popover-error">{error}</p>}
        <div className="popover-leave-actions">
          <button type="button" className="btn-primary" disabled={saveDisabled} onClick={onSave}>
            {saveLabel}
          </button>
          <button type="button" className="btn-secondary popover-danger" onClick={onDiscard}>
            {discardLabel}
          </button>
          <button ref={stayRef} type="button" className="btn-secondary" onClick={onStay}>
            {stayLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
